import {
  grantOf,
  grantsToApply,
  needsPlace,
  parseAccessGrant,
  parseTemplateGrant,
  templateGrants,
  type AccessGrant,
  type GrantScope,
  type RoleTemplate,
  type TemplateGrant,
} from '@church/permissions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { peopleKeys } from '../people/keys.ts'
import { useDebounced } from '../people/usePeople.ts'
import { servicesKeys } from '../services/useServices.ts'
import { sessionKeys } from '../session/keys.ts'
import { useSession } from '../session/useSession.ts'
import { accessKeys } from './keys.ts'
import { useServiceAccess } from './useAccess.ts'
import { outdent, toPlaceOption, type PlaceOption } from './useGrantForm.ts'

/**
 * Giving and taking away access (docs/13-ACCESS.md): a person's grants, a
 * folder's or service type's Access list, the people access can be given
 * to, and role templates. The server checks every grant (`canGrant`); these
 * hooks only carry them.
 *
 * Access is held by a login, or — for a directory person with no login yet
 * (2026-10-21) — waits for one (`pending`), moving onto their login when
 * they are linked: by an invitation for them, or an Owner or Admin linking
 * a login (`useLinkLogin`).
 */

/** Whom a grant is for: a login, or a directory person (who may have none yet). */
export type GrantTarget = { userId: string } | { personId: string }

/** A person's grants, by their login: all of your own; others' that you could give. */
export function useUserGrants(userId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: accessKeys.user(userId ?? ''),
    enabled: userId !== null,
    queryFn: async () => {
      const { data, error } = await api.api.v1.access.users({ id: userId ?? '' }).grants.get()
      if (error) throw error
      return data
    },
  })
}

export type GrantRow = NonNullable<ReturnType<typeof useUserGrants>['data']>[number]

/**
 * A directory person's access: their login's grants once they have one, or
 * what waits for them until they sign in (rows with `pending`).
 */
export function usePersonGrants(personId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: accessKeys.person(personId ?? ''),
    enabled: personId !== null,
    queryFn: async () => {
      const { data, error } = await api.api.v1.access.people({ id: personId ?? '' }).grants.get()
      if (error) throw error
      return data
    },
  })
}

/** Whom a row is held by, as a grant body names them. */
export const targetOf = (row: { userId: string | null; personId: string | null }) =>
  row.userId !== null
    ? { userId: row.userId }
    : row.personId !== null
      ? { personId: row.personId }
      : null

/** A folder's or service type's Access list; null when they may not see it (not an Administrator there). */
export function useScopeGrants(scope: { kind: 'folder' | 'service_type'; id: string } | null) {
  const api = useApi()
  return useQuery({
    queryKey: accessKeys.scope(scope?.kind ?? 'folder', scope?.id ?? ''),
    enabled: scope !== null,
    queryFn: async () => {
      if (!scope) return []
      const { data, error } =
        scope.kind === 'folder'
          ? await api.api.v1.access.folders({ id: scope.id }).grants.get()
          : await api.api.v1.access['service-types']({ id: scope.id }).grants.get()
      if (error) throw error
      return data
    },
  })
}

/**
 * Whom access can be given to (two letters at least): people with a login
 * here, by name or email, and people without one yet, by name
 * (`signedIn: false`; no email).
 */
export function useGrantablePeople(rawQuery: string) {
  const api = useApi()
  const q = useDebounced(rawQuery.trim(), 250)
  const query = useQuery({
    queryKey: accessKeys.people(q),
    enabled: q.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.api.v1.access.people.get({ query: { q } })
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
  return { results: query.data ?? [], isSearching: query.isFetching, error: query.error }
}

export type GrantablePerson = ReturnType<typeof useGrantablePeople>['results'][number]

const bodyOf = (target: GrantTarget, grant: AccessGrant) => ({
  ...target,
  module: grant.module,
  level: grant.level,
  scopeType: grant.scope.kind,
  scopeId: grant.scope.kind === 'org' ? null : grant.scope.id,
})

/**
 * After a change, every access list may be out of date — and the person's
 * own levels too, when it was theirs.
 */
function useRefresh() {
  const queryClient = useQueryClient()
  const { me } = useSession()
  return (target: GrantTarget | { userId: string | null }) => {
    void queryClient.invalidateQueries({ queryKey: accessKeys.all() })
    if ('userId' in target && target.userId === me?.user.id)
      void queryClient.invalidateQueries({ queryKey: sessionKeys.me() })
  }
}

/**
 * Gives someone a level somewhere. The same module and place again replaces
 * their level there; `replaces` (a grant moved to another place or module)
 * is taken away once the new one is given.
 */
export function useGiveAccess() {
  const api = useApi()
  const refresh = useRefresh()
  return useMutation({
    mutationFn: async (input: { to: GrantTarget; grant: AccessGrant; replaces?: string }) => {
      const { data, error } = await api.api.v1.access.grants.post(bodyOf(input.to, input.grant))
      if (error) throw error
      if (input.replaces && input.replaces !== data.id) {
        const removed = await api.api.v1.access.grants({ id: input.replaces }).delete()
        if (removed.error) throw removed.error
      }
      return data
    },
    onSettled: (_data, _error, input) => refresh(input.to),
  })
}

/** Takes a grant away (a login's, or one waiting for a login). */
export function useRemoveAccess() {
  const api = useApi()
  const refresh = useRefresh()
  return useMutation({
    mutationFn: async (input: { grantId: string; userId: string | null }) => {
      const { error } = await api.api.v1.access.grants({ id: input.grantId }).delete()
      if (error) throw error
    },
    onSettled: (_data, _error, input) => refresh(input),
  })
}

// ---- linking a person to a login -----------------------------------------------------------

/** Logins no person is linked to yet, by name or email (an Owner's or Admin's "Link to a login"). */
export function useUnlinkedLogins(rawQuery: string) {
  const api = useApi()
  const q = useDebounced(rawQuery.trim(), 250)
  const query = useQuery({
    queryKey: accessKeys.unlinkedLogins(q),
    enabled: q.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.api.v1.access.logins.unlinked.get({ query: { q } })
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
  return { results: query.data ?? [], isSearching: query.isFetching, error: query.error }
}

/**
 * Links a directory person to a login that has no person yet (Owner or
 * Admin): what waited for them moves onto the login, highest wins.
 */
export function useLinkLogin() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { personId: string; userId: string }) => {
      const { data, error } = await api.api.v1.access
        .people({ id: input.personId })
        .login.put({ userId: input.userId })
      if (error) throw error
      return data
    },
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: accessKeys.all() })
      // Their record now names the login.
      void queryClient.invalidateQueries({ queryKey: peopleKeys.detail(input.personId) })
      void queryClient.invalidateQueries({ queryKey: servicesKeys.all() })
    },
  })
}

// ---- role templates ----------------------------------------------------------------------

/** The built-in templates and the church's own (for whoever gives access or reads Settings). */
export function useAccessTemplates(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: accessKeys.templates(),
    enabled: options.enabled,
    queryFn: async (): Promise<RoleTemplate[]> => {
      const { data, error } = await api.api.v1.access.templates.get()
      if (error) throw error
      // The server parsed each grant; this narrows the strings back to the rules' types.
      return data.map((template) => ({
        ...template,
        grants: template.grants.flatMap((grant) => {
          const parsed = parseTemplateGrant(grant)
          return parsed ? [parsed] : []
        }),
      }))
    },
  })
}

export interface TemplateDraft {
  name: string
  description: string | null
  grants: TemplateGrant[]
}

/** Adding, changing and deleting the church's own templates (a Settings Administrator). */
export function useAccessTemplateEditor() {
  const api = useApi()
  const queryClient = useQueryClient()
  const refresh = () => void queryClient.invalidateQueries({ queryKey: accessKeys.templates() })
  const create = useMutation({
    mutationFn: async (draft: TemplateDraft) => {
      const { data, error } = await api.api.v1.access.templates.post({
        ...draft,
        grants: [...draft.grants],
      })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })
  const update = useMutation({
    mutationFn: async ({ id, ...draft }: TemplateDraft & { id: string }) => {
      const { data, error } = await api.api.v1.access
        .templates({ id })
        .patch({ ...draft, grants: [...draft.grants] })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.access.templates({ id }).delete()
      if (error) throw error
    },
    onSuccess: refresh,
  })
  return { create, update, remove }
}

/**
 * Applying a template to one person: what it would give at a place — less
 * what they already hold there at that level or higher, and split into what
 * the signed-in person may give and what they may not — and giving it, one
 * grant at a time. A person with no login yet gets it waiting for one.
 */
export function useApplyTemplate(personId: string | null) {
  const api = useApi()
  const refresh = useRefresh()
  const access = useServiceAccess()
  const held = usePersonGrants(personId)
  const heldGrants = (held.data ?? []).flatMap((row) => {
    const grant = parseAccessGrant(row)
    return grant ? [grant] : []
  })

  const plan = (template: RoleTemplate, place: GrantScope) => {
    const wanted = templateGrants(template, needsPlace(template) ? place : { kind: 'org' })
    const changes = grantsToApply(heldGrants, wanted)
    return {
      /** What it gives that they do not hold yet (or holds lower). */
      give: changes.filter((grant) => access.canGive(grant)),
      /** What it would give but the signed-in person may not. */
      beyond: changes.filter((grant) => !access.canGive(grant)),
      /** What they already hold there, at that level or higher. */
      held: wanted.filter((grant) => !changes.includes(grant)),
    }
  }

  /** Where it may be applied: the places they may give each of its "choose a place" grants. */
  const placesFor = (template: RoleTemplate): PlaceOption[] =>
    outdent(
      access.places
        .map(toPlaceOption)
        .filter((place) =>
          template.grants
            .filter((grant) => grant.scope === 'pick')
            .every((grant) => access.canGive(grantOf(grant.module, grant.level, place.scope))),
        ),
    )

  const apply = useMutation({
    mutationFn: async (grants: readonly AccessGrant[]) => {
      if (!personId) return 0
      for (const grant of grants) {
        const { error } = await api.api.v1.access.grants.post(bodyOf({ personId }, grant))
        if (error) throw error
      }
      return grants.length
    },
    onSettled: () => {
      // Their own access may be what changed: refresh everything access shows.
      refresh({ userId: held.data?.find((row) => row.userId !== null)?.userId ?? null })
    },
  })

  return { plan, placesFor, apply, ready: held.isSuccess && access.ready }
}
